import { useState } from "react";
import { HelpCircle, RefreshCw, Sparkles } from "lucide-react";

const sel = "rounded-md border border-zinc-800 bg-[#0b0b0c] px-3 py-1.5 text-xs text-slate-200 outline-none";
const ORDER = ["response_time", "notes_quality", "advancement", "conversion"];

function Bars({ s, info }) {
  return (
    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4" data-testid={`seller-components-${s.ghl_id}`}>
      {ORDER.map((k) => {
        const on = s.measured?.[k];
        return (
          <div key={k} title={info[k]?.desc}>
            <div className="flex justify-between text-xs text-slate-500"><span>{info[k]?.label}</span><span className="tabular-nums">{on ? `${s.components[k]}/25` : "N/D"}</span></div>
            <div className="mt-0.5 h-1 overflow-hidden rounded-md bg-zinc-800">
              {on && <div className="h-full bg-[#e5e5ea]" style={{ width: `${(s.components[k] / 25) * 100}%` }} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TeamQuality({ quality, pipelines, pipelineId, setPipelineId, onRecalc, periodLabel }) {
  const [help, setHelp] = useState(false);
  const info = quality.components_info || {};
  const scored = quality.sellers.filter((s) => s.measured && Object.values(s.measured).some(Boolean));
  const idle = quality.sellers.length - scored.length;
  const top = scored.slice(0, 5);
  return (
    <div className="athanor-card p-6 fade-up fade-up-2" data-testid="team-quality-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-100"><Sparkles className="h-4 w-4 text-[#C5A059]" /> Qualità Team</h2>
          <p className="text-xs text-slate-500">Score 0-100 dei commerciali attivi per {periodLabel}.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={pipelineId} onChange={(e) => setPipelineId(e.target.value)} data-testid="quality-pipeline-select" className={sel}>
            <option value="all">Tutte le pipeline</option>
            {pipelines.map((p) => <option key={p.ghl_id} value={p.ghl_id}>{p.name}</option>)}
          </select>
          <button onClick={() => setHelp((v) => !v)} data-testid="quality-help-button" title="Come si calcola lo score"
            className={`rounded-md border px-2 py-1.5 text-xs ${help ? "border-[#C5A059]/60 text-[#C5A059]" : "border-zinc-800 text-slate-400 hover:text-slate-100"}`}>
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
          <button onClick={onRecalc} data-testid="recalc-all-button"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#C5A059]/40 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-[#C5A059]/10">
            <RefreshCw className="h-3.5 w-3.5" /> Ricalcola tutti
          </button>
        </div>
      </div>

      {help && (
        <div className="mt-4 rounded-lg border border-[#C5A059]/20 bg-[#C5A059]/5 p-4 text-xs text-slate-300" data-testid="quality-help-panel">
          <div className="mb-2 font-semibold text-slate-100">Come sale il livello</div>
          <p className="mb-3 text-slate-400">Ogni metrica vale fino a 25 punti. Le metriche per cui GHL non ha ancora dati sufficienti (N/D) non contano: lo score è la media in centesimi delle sole metriche misurabili. Un commerciale che lavora regolarmente i lead si posiziona tra 70 e 100.</p>
          <ul className="space-y-1.5">
            {ORDER.map((k) => <li key={k}><span className="text-[#C5A059]">{info[k]?.label}</span> — {info[k]?.desc}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-[180px_1fr]">
        <div>
          <div className="text-xs text-slate-500">Score medio</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="tabular-nums text-3xl font-bold text-slate-50" data-testid="team-avg">{scored.length ? quality.team_avg : "—"}</span>
            <span className="text-xs text-slate-500">su 100</span>
          </div>
          <div className="mt-2 text-xs text-slate-500" data-testid="team-scored-count">{scored.length} commerciali attivi nel periodo</div>
          {idle > 0 && <div className="text-xs text-slate-600" data-testid="team-idle-count">{idle} senza lead nel periodo (esclusi)</div>}
        </div>
        <div>
          <div className="text-xs text-slate-500">Classifica</div>
          {top.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500" data-testid="team-quality-empty">Nessun commerciale con lead assegnati in questa pipeline nel periodo.</p>
          ) : (
            <div className="mt-2 space-y-1.5">
              {top.map((s, i) => (
                <a key={s.ghl_id} href={`/venditori/${s.ghl_id}`} data-testid={`seller-row-${s.ghl_id}`}
                  className="block rounded-md border border-zinc-800/70 px-3 py-2 hover:border-[#C5A059]/40">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-100"><span className="mr-2 tabular-nums text-slate-500">#{i + 1}</span>{s.name}
                      <span className="ml-2 text-xs text-slate-500">{s.counts?.opportunita} lead · {s.status_label}</span></span>
                    <span className="flex items-center gap-2">
                      {s.maturity_pct != null && s.maturity_pct < 100 && s.counts?.vinte < 2 && (
                        <span className="whitespace-nowrap rounded-full border border-amber-500/40 px-2 py-0.5 text-xs text-amber-300" title={`Punteggio pieno ${s.raw_total}, scalato al ${s.maturity_pct}% per pochi lead nel periodo`} data-testid={`seller-maturity-${s.ghl_id}`}>dati {s.maturity_pct}%</span>
                      )}
                      <span className="tabular-nums text-sm font-bold text-[#C5A059]" data-testid={`seller-score-${s.ghl_id}`}>{s.total}</span>
                    </span>
                  </div>
                  <Bars s={s} info={info} />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
